import { Card, Col, Row, Statistic } from "antd";
import { DynamicMetaTags } from "../utils/dynamicMetaTags";
import "./Biography.css";
import "./EndowmentFund.css";

const pageTitle = "Eng. Nahil Wijesuriya Endowment Fund | Nahil Wijesuriya";
const pageDescription =
  "The Eng. Nahil Wijesuriya Endowment Fund is a permanent LKR 3 billion educational endowment supporting students, teachers and the wider Trinity College Kandy community.";

const fundPrinciples = [
  {
    title: "Preserve",
    text: "Protect the underlying capital and preserve its long-term ability to support future beneficiaries.",
  },
  {
    title: "Grow",
    text: "Invest prudently and responsibly with proper diversification, sustainable income, and long-term growth.",
  },
  {
    title: "Support",
    text: "Apply available income fairly and transparently to the educational and charitable purposes defined by the Trust.",
  },
];

const programAreas = [
  {
    title: "Educational assistance",
    text: "Support eligible children of Old Trinitians admitted to Trinity College Kandy, with assistance of up to 100% of eligible fees where appropriate.",
  },
  {
    title: "Wider access to education",
    text: "Provide support to children outside the Old Trinitian community based on financial need and the trustees' eligibility criteria.",
  },
  {
    title: "Supporting educators",
    text: "Assist current or retired permanent academic staff facing financial hardship or extraordinary medical costs.",
  },
  {
    title: "Academic excellence",
    text: "Fund incentives for outstanding teachers and initiatives designed to attract and retain exceptional academic staff.",
  },
  {
    title: "Sports scholarships",
    text: "Recognise talented athletes whose participation strengthens Trinity's sporting standards.",
  },
  {
    title: "Community and humanitarian initiatives",
    text: "Support disadvantaged schools and underserved communities where resources allow.",
  },
];

const governanceAreas = [
  "Protecting the Fund's capital",
  "Establishing investment and distribution policies",
  "Appointing professional managers and advisers",
  "Determining eligibility and assistance criteria",
  "Reviewing and approving disbursements",
  "Managing conflicts of interest",
  "Maintaining proper financial records",
  "Producing annual accounts and activity reports",
  "Ensuring the Fund remains faithful to its founding purpose",
];

export const EndowmentFund = () => {
  return (
    <>
      <DynamicMetaTags
        ogTitle={pageTitle}
        ogDescription={pageDescription}
        ogUrl="nahilwijesuriya.com/nahil-wijesuriya-endowment-fund"
      />
      <div
        className="biography-page endowment-page"
        style={{
          background: "#f8f3ec",
          minHeight: "100vh",
          padding: "32px 16px 56px",
        }}
      >
        <div
          className="endowment-layout"
          style={{ maxWidth: "1180px", margin: "0 auto" }}
        >
          <section
            className="endowment-hero"
            style={{
              background: "#fffdfb",
              border: "1px solid #eadfce",
              padding: "40px 32px",
              marginBottom: "24px",
              boxShadow: "0 8px 24px rgba(72, 52, 27, 0.04)",
            }}
          >
            <p
              style={{
                color: "#8a5b25",
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.08em",
                marginBottom: "10px",
                textTransform: "uppercase",
              }}
            >
              Endowment fund
            </p>

            <h1
              style={{
                fontSize: "clamp(2.2rem, 4vw, 4rem)",
                lineHeight: 1.1,
                margin: "0 0 18px",
                color: "#1d1d1d",
              }}
            >
              The Eng. Nahil Wijesuriya Endowment Fund
            </h1>

            <h2
              style={{
                fontSize: "clamp(1.3rem, 2vw, 2.2rem)",
                lineHeight: 1.4,
                marginBottom: "16px",
                color: "#2b2b2b",
                fontWeight: 500,
              }}
            >
              A permanent endowment for education, opportunity and service
            </h2>

            <p
              style={{
                fontSize: "18px",
                lineHeight: 1.8,
                marginBottom: "14px",
              }}
            >
              The Eng. Nahil Wijesuriya Endowment Fund is being established with
              an initial endowment of LKR 3 billion to support students,
              educators and communities connected with Trinity College Kandy.
            </p>

            <p style={{ fontSize: "18px", lineHeight: 1.8, marginBottom: "0" }}>
              Designed to endure across generations, the Fund preserves its
              underlying capital while using sustainably generated income to
              expand access to education, recognise exceptional ability and
              support those facing financial hardship.
            </p>

            <Row
              className="endowment-facts"
              gutter={[16, 16]}
              style={{ marginTop: "24px" }}
            >
              <Col xs={24} md={6}>
                <Statistic title="Initial endowment" value="LKR 3B" />
              </Col>
              <Col xs={24} md={6}>
                <Statistic title="Primary focus" value="Education" />
              </Col>
              <Col xs={24} md={6}>
                <Statistic title="Beneficiaries" value="Students" />
              </Col>
              <Col xs={24} md={6}>
                <Statistic title="Institution" value="Trinity" />
              </Col>
            </Row>
          </section>

          <section
            className="endowment-principles"
            style={{ marginBottom: "24px" }}
          >
            <Row gutter={[16, 16]}>
              {fundPrinciples.map((item) => (
                <Col xs={24} md={8} key={item.title}>
                  <Card title={item.title} style={{ height: "100%" }}>
                    <p style={{ lineHeight: 1.7, marginBottom: 0 }}>
                      {item.text}
                    </p>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>

          <section
            className="endowment-section"
            style={{
              background: "#fff",
              border: "1px solid #eadfce",
              padding: "32px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{ fontSize: "28px", marginTop: 0, marginBottom: "16px" }}
            >
              A lasting commitment to education
            </h3>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              Education has the power to shape individual lives, strengthen
              institutions and create opportunities that extend far beyond the
              classroom. The Fund has been conceived as a permanent and
              independently governed source of educational support, intended to
              help ensure that future generations can benefit from the values,
              opportunities and standards associated with Trinity College Kandy.
            </p>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              It reflects Eng. Nahil Wijesuriya's longstanding relationship with
              the College and his belief that access to education should be
              supported with dignity, foresight and responsible stewardship.
            </p>
          </section>

          <section
            className="endowment-section endowment-section-tinted"
            style={{
              background: "#fff",
              border: "1px solid #eadfce",
              padding: "32px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{ fontSize: "28px", marginTop: 0, marginBottom: "18px" }}
            >
              The purpose of the Fund
            </h3>
            <Row className="endowment-programs" gutter={[16, 16]}>
              {programAreas.map((item) => (
                <Col xs={24} md={12} key={item.title}>
                  <Card
                    title={item.title}
                    style={{ height: "100%", borderRadius: 0 }}
                  >
                    <p style={{ lineHeight: 1.7, marginBottom: 0 }}>
                      {item.text}
                    </p>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>

          <section
            className="endowment-section endowment-section-dark"
            style={{
              background: "#fff",
              border: "1px solid #eadfce",
              padding: "32px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{ fontSize: "28px", marginTop: 0, marginBottom: "18px" }}
            >
              How the endowment will work
            </h3>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              The LKR 3 billion endowment is intended to remain invested as
              permanent capital. Rather than spending the original contribution,
              the Fund will ordinarily use income and investment returns
              generated from it to finance scholarships, fee assistance, staff
              support and other approved programmes.
            </p>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              This structure is designed to create a sustainable source of
              support that can continue across generations while preserving the
              original gift for the long term.
            </p>
          </section>

          <section
            className="endowment-section"
            style={{
              background: "#fff",
              border: "1px solid #eadfce",
              padding: "32px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{ fontSize: "28px", marginTop: 0, marginBottom: "18px" }}
            >
              Independent governance
            </h3>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              The Fund will be administered by an independent Board of Trustees
              with experience spanning education, finance, law, banking,
              business and institutional governance.
            </p>
            <ul
              style={{ fontSize: "17px", lineHeight: 1.9, paddingLeft: "20px" }}
            >
              {governanceAreas.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section
            className="endowment-section endowment-section-tinted"
            style={{
              background: "#fff",
              border: "1px solid #eadfce",
              padding: "32px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{ fontSize: "28px", marginTop: 0, marginBottom: "18px" }}
            >
              Relationship with Trinity College Kandy
            </h3>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              The Fund is intended to support eligible students, educators and
              approved educational initiatives associated with Trinity College
              while remaining independently governed. It will not operate as a
              general source of finance for the College's normal operating
              expenditure, deficits or financial obligations.
            </p>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              Any payment made to the College on behalf of a beneficiary will
              represent assistance provided to that individual, not unrestricted
              institutional funding.
            </p>
          </section>

          <section
            className="endowment-section"
            style={{
              background: "#fff",
              border: "1px solid #eadfce",
              padding: "32px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{ fontSize: "28px", marginTop: 0, marginBottom: "18px" }}
            >
              Accountability and transparency
            </h3>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              Proper stewardship is central to the Fund's long-term purpose. The
              Fund will maintain dedicated financial and investment accounts,
              accurate records of receipts and payments, and appropriate
              documentation for every award or disbursement.
            </p>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              Its governance framework provides for annual financial statements,
              independent audit, investment-performance reporting, annual
              reporting on activities and beneficiaries, and formal declaration
              of trustee conflicts.
            </p>
          </section>

          <section
            className="endowment-section endowment-section-dark"
            style={{
              background: "#fff",
              border: "1px solid #eadfce",
              padding: "32px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{ fontSize: "28px", marginTop: 0, marginBottom: "18px" }}
            >
              Looking to the future
            </h3>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              The Eng. Nahil Wijesuriya Endowment Fund is intended to be more
              than a single donation. It is a long-term commitment to
              educational opportunity, institutional excellence, and responsible
              philanthropy.
            </p>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              By protecting its capital and investing for the future, the Fund
              aims to support thousands of individual journeys over its
              lifetime, helping students enter the classroom, enabling talent to
              develop and recognising the educators who make those opportunities
              possible.
            </p>
          </section>

          <section
            className="endowment-section"
            style={{
              background: "#fff",
              border: "1px solid #eadfce",
              padding: "32px",
              marginBottom: "24px",
            }}
          >
            <h3
              style={{ fontSize: "28px", marginTop: 0, marginBottom: "18px" }}
            >
              Applications and eligibility
            </h3>
            <p style={{ fontSize: "17px", lineHeight: 1.8 }}>
              The Fund is currently progressing through its establishment and
              governance process. Eligibility criteria, application procedures
              and supporting documentation will be published once the Fund
              becomes operational.
            </p>
            <p style={{ color: "#8a5b25", fontSize: "17px", fontWeight: 700 }}>
              Application status: Not yet open
            </p>
          </section>

          <p
            className="endowment-disclaimer"
            style={{ color: "#6f6a63", fontSize: "13px", lineHeight: 1.7 }}
          >
            The information presented on this page is a public summary of the
            proposed purposes and governance principles of the Eng. Nahil
            Wijesuriya Endowment Fund. It does not replace or modify the Fund's
            executed Deed of Trust, formal policies or applicable law.
          </p>
        </div>
      </div>
    </>
  );
};
